import { useState } from "react";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useSpring, animated } from '@react-spring/three';
import { Center, Text3D } from "@react-three/drei";
import layout from "../../dictionaries/layout.js";
import translations from "../../dictionaries/translations.js";
import { 
  deviceAtom, 
  gamePhaseAtom,
  languageAtom,
  languageModalOpenAtom,
} from "../../GlobalState.jsx";
import useSoundEffectsPlayer from "../../soundPlayers/useSoundEffectsPlayer.jsx";
import { trackEvent } from '../../api';

export default function LanguageModalAnimated({ position, rotation, scale, animate = true }) {
  const languageModalOpen = useAtomValue(languageModalOpenAtom)
  const device = useAtomValue(deviceAtom)
  const gamePhase = useAtomValue(gamePhaseAtom)
  const [language, setLanguage] = useAtom(languageAtom)
  const setLanguageModalOpen = useSetAtom(languageModalOpenAtom)
  const { playSoundEffect } = useSoundEffectsPlayer()

  const springs = useSpring({
    scale: languageModalOpen ? 1 : 0,
    config: {
      tension: 170,
      friction: 26
    }
  })

  const modalPosition = position ?? layout[device].lobby.languageModal.position
  const modalRotation = rotation ?? [0, 0, 0]
  const modalScale = scale ?? layout[device].lobby.languageModal.scale
  const surface = gamePhase === 'lobby'
    ? 'lobby'
    : gamePhase === 'finished'
      ? 'end_scene'
      : gamePhase === 'minigame'
        ? 'minigame'
        : gamePhase === 'pregame' || gamePhase === 'game'
          ? 'game'
          : 'home'

  function Language({ position, rotation, scale }) {
    
    const textFont = language === 'korean'
      ? "/fonts/Maplestory_Bold.json"
      : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"
    
    function LanguageOptionButton({ position, languageCode, displayText }) {
      const [hover, setHover] = useState(false)
  
      function handlePointerEnter(e) {
        e.stopPropagation()
        setHover(true)
        document.body.style.cursor = 'pointer'
      }
      function handlePointerLeave(e) {
        e.stopPropagation()
        setHover(false)
        document.body.style.cursor = 'default'
      }
      function handlePointerDown(e) {
        e.stopPropagation()
        setLanguage(languageCode)
        playSoundEffect('/sounds/effects/button-click.mp3', 1)
        trackEvent({ surface, action: 'change_language', category: 'ui', metadata: { language: languageCode } })
      }
      
      const textFont = languageCode === 'korean'
        ? "/fonts/Maplestory_Bold.json"
        : languageCode === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"

      return <group position={position}>
        {/* background */}
        <group name='background'>
          <mesh name='background-outer' scale={[4.5, 0.01, 0.67]}>
            <boxGeometry args={[1,1,1]}/>
            <meshStandardMaterial color={ hover || language === languageCode ? 'green' : 'orange' }/>
          </mesh>
          <mesh name='background-inner' scale={[4.4, 0.02, 0.57]}>
            <boxGeometry args={[1,1,1]}/>
            <meshStandardMaterial color='black'/>
          </mesh>
          <mesh name='wrapper'
            onPointerEnter={e => handlePointerEnter(e)}
            onPointerLeave={e => handlePointerLeave(e)}
            onPointerDown={e => handlePointerDown(e)}
            scale={[4.5, 0.02, 0.42]}
          >
            <boxGeometry args={[1,1,1]}/>
            <meshStandardMaterial color='black' transparent opacity={0}/>
          </mesh>
        </group>
        {/* text */}
        <Center position={[0, 0.02, 0]} rotation={[-Math.PI/2, 0, 0]}>
          <Text3D font={textFont} size={0.35} height={0.01}>
            {displayText}
            <meshStandardMaterial color={ hover || language === languageCode ? 'green' : 'orange' }/>
          </Text3D>
        </Center>
      </group>
    }

    function CloseButton({ position, scale  }) {
      const [hover, setHover] = useState(false)
  
      function handlePointerEnter(e) {
        e.stopPropagation()
        setHover(true)
        document.body.style.cursor = 'pointer'
      }
      function handlePointerLeave(e) {
        e.stopPropagation()
        setHover(false)
        document.body.style.cursor = 'default'
      }
      function handlePointerDown(e) {
        e.stopPropagation()
        setLanguageModalOpen(false)
        playSoundEffect('/sounds/effects/button-click.mp3', 1)
      }
  
      return <group position={position} scale={scale}>
        {/* background */}
        <group name='background'>
          <mesh name='background-outer' scale={[1.55, 0.01, 0.55]}>
            <boxGeometry args={[1,1,1]}/>
            <meshStandardMaterial color={ hover ? 'green' : 'orange' }/>
          </mesh>
          <mesh name='background-inner' scale={[1.45, 0.02, 0.45]}>
            <boxGeometry args={[1,1,1]}/>
            <meshStandardMaterial color='black'/>
          </mesh>
          <mesh name='background-wrapper' 
          scale={[1.55, 0.02, 0.55]}
          onPointerEnter={e=>handlePointerEnter(e)}
          onPointerLeave={e=>handlePointerLeave(e)}
          onPointerDown={e=>handlePointerDown(e)}>
            <boxGeometry args={[1,1,1]}/>
            <meshStandardMaterial transparent opacity={0}/>
          </mesh>
        </group>
        {/* text */}
        <Text3D
          font="/fonts/Luckiest Guy_Regular.json"
          position={[-0.63,0.02,0.14]}
          rotation={[-Math.PI/2, 0, 0]}
          size={0.26}
          height={0.01}
        >
          X CLOSE
          <meshStandardMaterial color={ hover ? 'green' : 'orange' }/>
        </Text3D>
      </group>
    }

    return <group position={position} rotation={rotation} scale={scale}>
      {/* background */}
      <group name='background'>
        <mesh name='background-outer' scale={[5, 0.01, 4.2]}>
          <boxGeometry args={[1,1,1]}/>
          <meshStandardMaterial color='orange'/>
        </mesh>
        <mesh name='background-inner' scale={[4.9, 0.02, 4.1]}>
          <boxGeometry args={[1,1,1]}/>
          <meshStandardMaterial color='black'/>
        </mesh>
      </group>
      {/* title */}
      <Text3D
        font={textFont}
        position={[-2.3, 0.02, -1.45]}
        rotation={[-Math.PI/2, 0, 0]}
        size={0.4}
        height={0.01}
      >
        {translations.language[language]}
        <meshStandardMaterial color='orange'/>
      </Text3D>
      {/* close button */}
      <CloseButton position={[1.65, 0.02, -1.65]} scale={0.9}/>
      {/* language buttons */}
      <LanguageOptionButton 
        position={[0, 0.02, -0.9]} 
        languageCode='english' 
        displayText={translations.languages.english}
      />
      <LanguageOptionButton 
        position={[0, 0.02, -0.1]} 
        languageCode='korean' 
        displayText={translations.languages.korean}
      />
      <LanguageOptionButton 
        position={[0, 0.02, 0.7]} 
        languageCode='spanish' 
        displayText={translations.languages.spanish}
      />
      <LanguageOptionButton 
        position={[0, 0.02, 1.5]}
        languageCode='chinese'
        displayText={translations.languages.chinese}
      />
    </group>
  }

  if (!animate) {
    return languageModalOpen && <Language
      position={modalPosition}
      rotation={modalRotation}
      scale={modalScale}
    />
  }

  return <animated.group scale={springs.scale}>
    <Language
      position={modalPosition}
      rotation={modalRotation}
      scale={modalScale}
    />
  </animated.group>
}
